import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, CheckCircle2, PauseCircle, Inbox, User, ChevronLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import NavBar from "@/components/NavBar";

/**
 * iter72j · Super Admin Support Inbox.
 *
 * Queue view with tabs (New / In Progress / Waiting / Resolved) and a
 * "mine only" toggle. Each row shows priority, SLA countdown, submitter,
 * subject, assignee. Click a row → SA ticket detail page.
 */

const STATUS_TABS = [
  { key: "new", label: "New", icon: Inbox },
  { key: "in_progress", label: "In Progress", icon: Clock },
  { key: "waiting", label: "Waiting", icon: PauseCircle },
  { key: "resolved", label: "Resolved", icon: CheckCircle2 },
];

const PRIORITY_COLORS = {
  urgent: "bg-[#8a1a1a] text-white",
  high: "bg-[#B59A5A] text-white",
  normal: "bg-[#1A422D] text-white",
  low: "bg-[#7A7A7A] text-white",
};

export default function SuperAdminSupportPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("new");
  const [mine, setMine] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ new: 0, in_progress: 0, waiting: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/support/tickets", {
        params: { status: tab, mine: mine || undefined },
      });
      setTickets(data.tickets || []);
      setCounts(data.counts || {});
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, mine]);

  return (
    <>
      <NavBar />
      <main className="max-w-7xl mx-auto px-5 sm:px-10 py-10" data-testid="sa-support-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Support</div>
            <h1 className="font-serif text-4xl text-[#1A422D] mt-2">Inbound Support Inbox</h1>
            <p className="mt-2 text-sm text-[#4A4A4A] max-w-2xl">
              Tickets from clubs, vendors, and members. SLA-managed with
              auto-escalation. Assign to the right teammate and keep the
              queue moving.
            </p>
          </div>
          <Link to="/super-admin" className="btn-secondary text-xs" data-testid="sa-support-back">
            <ChevronLeft size={12} className="inline" /> Console
          </Link>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {STATUS_TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                      data-testid={`sa-support-tab-${t.key}`}
                      className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] border transition ${
                        active
                          ? "border-[#1A422D] bg-[#1A422D] text-[#F9F8F4]"
                          : "border-[#E5E3D8] text-[#4A4A4A] hover:border-[#1A422D] hover:text-[#1A422D]"
                      }`}>
                <t.icon size={11} /> {t.label} ({counts[t.key] || 0})
              </button>
            );
          })}
          <label className="inline-flex items-center gap-2 text-xs text-[#4A4A4A] ml-auto">
            <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)}
                   data-testid="sa-support-mine" className="h-4 w-4 accent-[#1A422D]" />
            Only mine
          </label>
        </div>

        {err && (
          <div className="mt-5 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-xs flex items-center gap-2">
            <AlertTriangle size={12} /> {err}
          </div>
        )}

        <div className="mt-6 surface-card overflow-x-auto">
          <table className="w-full text-left" data-testid="sa-support-list">
            <thead className="bg-[#F3F1E9]">
              <tr className="text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A]">
                <th className="px-5 py-3">Priority</th>
                <th className="px-5 py-3">Subject</th>
                <th className="px-5 py-3">Submitter</th>
                <th className="px-5 py-3">Assigned</th>
                <th className="px-5 py-3">SLA</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={5} className="px-5 py-8 text-center text-[#7A7A7A]">Loading…</td></tr>)}
              {!loading && tickets.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-[#7A7A7A]" data-testid="sa-support-empty">
                  No {tab.replace("_", " ")} tickets.
                </td></tr>
              )}
              {tickets.map((t) => (
                <tr key={t.ticket_id}
                    className="border-t border-[#E5E3D8] cursor-pointer hover:bg-[#F9F8F4] transition"
                    onClick={() => nav(`/super-admin/support/${t.ticket_id}`)}
                    data-testid={`sa-support-row-${t.ticket_id}`}>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${PRIORITY_COLORS[t.priority] || PRIORITY_COLORS.normal}`}>
                      {t.priority}
                    </span>
                    {t.escalated_count > 0 && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-[0.16em] text-[#8a1a1a]">
                        · ↑{t.escalated_count}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="font-serif text-[#1A422D]">{t.subject}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">
                      {t.ticket_id}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <div>{t.submitter?.name}</div>
                    <div className="text-[11px] text-[#7A7A7A]">
                      {t.submitter?.role} · {t.submitter?.email}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm">
                    {t.assigned_to_name || <span className="text-[#7A7A7A]">Unassigned</span>}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <SlaChip ticket={t} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </>
  );
}

function SlaChip({ ticket }) {
  const responded = !!ticket.first_responded_at;
  if (responded) return <span className="text-[10px] uppercase tracking-[0.18em] text-[#1A422D] inline-flex items-center gap-1"><CheckCircle2 size={10} /> Responded</span>;
  const due = ticket.sla_due_at ? new Date(ticket.sla_due_at).getTime() : null;
  if (!due) return <span className="text-[10px] text-[#7A7A7A]">—</span>;
  const now = Date.now();
  const diff = due - now;
  const abs = Math.abs(diff);
  const hours = Math.floor(abs / 36e5);
  const mins = Math.floor((abs % 36e5) / 6e4);
  const pretty = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  if (diff < 0) return <span className="text-[10px] uppercase tracking-[0.18em] text-[#8a1a1a] inline-flex items-center gap-1"><AlertTriangle size={10} /> Breached {pretty} ago</span>;
  const soon = diff < 60 * 60 * 1000;
  return <span className={`text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1 ${soon ? "text-[#8a6f33]" : "text-[#4A4A4A]"}`}><Clock size={10} /> Due in {pretty}</span>;
}
